"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMClassBytecodeCompiler = void 0;
const inversify_1 = require("inversify");
const IVMCompilerUtils_1 = require("../../../interfaces/vm-transformers/IVMCompilerUtils");
const ServiceIdentifiers_1 = require("../../../container/ServiceIdentifiers");
const NodeGuards_1 = require("../../../node/NodeGuards");
const NodeFactory_1 = require("../../../node/NodeFactory");
const VMOpCode_1 = require("../../../enums/vm-transformers/VMOpCode");
const VMConstants_1 = require("../../VMConstants");
let VMClassBytecodeCompiler = class VMClassBytecodeCompiler {
    constructor(vmCompilerUtils) {
        this.vmCompilerUtils = vmCompilerUtils;
    }
    compileClassDeclaration(node, context) {
        var _a, _b;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        this.compileClassCommon(node, context);
        if (node.id) {
            const className = node.id.name;
            const isTopLevelClass = (_a = context.topLevelVariables) === null || _a === void 0 ? void 0 : _a.has(className);
            if (isTopLevelClass) {
                builder.storeGlobalByName(className);
            }
            else {
                const scopedKey = (0, VMConstants_1.getScopedVariableName)(className, context.blockScopeDepth);
                const localIndex = this.vmCompilerUtils.getNextLocalIndex(context);
                context.localVariables.set(scopedKey, localIndex);
                context.variableScopes.set(scopedKey, {
                    depth: context.blockScopeDepth,
                    index: localIndex
                });
                (_b = context.activeBlockScopes) === null || _b === void 0 ? void 0 : _b.add(scopedKey);
                const needsEnvironmentStorage = context.envVars.has(className);
                if (needsEnvironmentStorage) {
                    const usesScopedKey = context.blockScopeDepth > 1;
                    const envKey = usesScopedKey ? scopedKey : className;
                    const nameIndex = context.constantPool.add(envKey);
                    builder.storeToCurrentEnv(nameIndex);
                }
                else {
                    const actualIndex = this.vmCompilerUtils.getActualLocalIndex(localIndex, context);
                    builder.storeLocal(actualIndex);
                }
            }
        }
    }
    compileClassExpression(node, context) {
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        let internalKey;
        let originalAliasMap;
        if (node.id) {
            const className = node.id.name;
            internalKey = `__classExpr_${className}_${context.nextBytecodeId++}__`;
            originalAliasMap = context.classExpressionNameAliases;
            context.classExpressionNameAliases = new Map(originalAliasMap);
            context.classExpressionNameAliases.set(className, internalKey);
            context.envVars.add(internalKey);
        }
        this.compileClassCommon(node, context);
        if (originalAliasMap !== undefined) {
            context.classExpressionNameAliases = originalAliasMap;
        }
        else if (internalKey) {
            context.classExpressionNameAliases = undefined;
        }
        if (node.id) {
            const className = node.id.name;
            if (internalKey) {
                const nameIndex = context.constantPool.add(internalKey);
                builder.dup();
                builder.storeToCurrentEnv(nameIndex);
            }
            else if (this.vmCompilerUtils.isEnvVariable(className, context)) {
                const nameIndex = context.constantPool.add(className);
                builder.dup();
                builder.storeToEnv(nameIndex);
            }
            else {
                if (!context.localVariables.has(className)) {
                    const localIndex = context.localVariables.size;
                    context.localVariables.set(className, localIndex);
                }
                const localIndex = context.localVariables.get(className);
                const actualIndex = this.vmCompilerUtils.getActualLocalIndex(localIndex, context);
                builder.dup();
                builder.storeLocal(actualIndex);
            }
        }
    }
    compileClassCommon(node, context) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        const constructorMethod = node.body.body.find((member) => NodeGuards_1.NodeGuards.isMethodDefinitionNode(member) && member.kind === 'constructor');
        const instanceFields = node.body.body.filter((member) => NodeGuards_1.NodeGuards.isPropertyDefinitionNode(member) && !member.static && !!member.value);
        if (constructorMethod && NodeGuards_1.NodeGuards.isFunctionExpressionNode(constructorMethod.value)) {
            const modifiedConstructor = this.injectInstanceFieldsIntoConstructor(constructorMethod.value, instanceFields, Boolean(node.superClass));
            (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(modifiedConstructor);
        }
        else if (instanceFields.length > 0) {
            const syntheticConstructor = this.createConstructorWithInstanceFields(instanceFields, Boolean(node.superClass));
            (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.expression(syntheticConstructor);
        }
        else if (node.superClass) {
            const syntheticConstructor = this.createConstructorWithInstanceFields([], true);
            (_c = context.dispatch) === null || _c === void 0 ? void 0 : _c.expression(syntheticConstructor);
        }
        else {
            builder.pushUndefined();
        }
        if (node.id) {
            const nameIndex = context.constantPool.add(node.id.name);
            builder.loadConstByIndex(nameIndex);
        }
        else {
            builder.pushNull();
        }
        const isDerived = node.superClass ? 1 : 0;
        builder.emitRaw({ op: VMOpCode_1.VMOpCode.NEW_CLASS, arg: isDerived });
        if (node.superClass) {
            (_d = context.dispatch) === null || _d === void 0 ? void 0 : _d.expression(node.superClass);
            builder.dup();
            builder.storeGlobalByName('__$$super$$__');
            builder.pop();
            builder.emitRaw({ op: VMOpCode_1.VMOpCode.CLASS_EXTENDS, arg: 0 });
        }
        const hasStaticBlocks = node.body.body.some((member) => NodeGuards_1.NodeGuards.isStaticBlockNode(member));
        const hasStaticFields = node.body.body.some((member) => NodeGuards_1.NodeGuards.isPropertyDefinitionNode(member) && member.static && member.value);
        if (node.id && (hasStaticBlocks || hasStaticFields)) {
            const className = node.id.name;
            builder.dup();
            const internalKey = (_e = context.classExpressionNameAliases) === null || _e === void 0 ? void 0 : _e.get(className);
            if (internalKey) {
                const nameIndex = context.constantPool.add(internalKey);
                builder.storeToCurrentEnv(nameIndex);
            }
            else if (context.envVars.has(className)) {
                const nameIndex = context.constantPool.add(className);
                builder.storeToCurrentEnv(nameIndex);
            }
            else {
                if (!context.localVariables.has(className)) {
                    const maxIndex = Math.max(-1, ...Array.from(context.localVariables.values()));
                    const localIndex = maxIndex + 1;
                    context.localVariables.set(className, localIndex);
                }
                const localIndex = context.localVariables.get(className);
                const actualIndex = this.vmCompilerUtils.getActualLocalIndex(localIndex, context);
                builder.storeLocal(actualIndex);
            }
        }
        for (const member of node.body.body) {
            if (NodeGuards_1.NodeGuards.isStaticBlockNode(member)) {
                builder.dup();
                const staticBlockFunc = NodeFactory_1.NodeFactory.functionExpressionNode([], NodeFactory_1.NodeFactory.blockStatementNode(member.body));
                (_f = context.dispatch) === null || _f === void 0 ? void 0 : _f.expression(staticBlockFunc);
                builder.loadConst(0);
                builder.callMethod(0);
                builder.pop();
                continue;
            }
            if (NodeGuards_1.NodeGuards.isPropertyDefinitionNode(member)) {
                if (member.static && member.value) {
                    builder.dup();
                    (_g = context.dispatch) === null || _g === void 0 ? void 0 : _g.expression(member.value);
                    if (member.computed) {
                        builder.pop();
                        builder.pop();
                    }
                    else {
                        const fieldName = this.getPropertyName(member);
                        if (!fieldName) {
                            builder.pop();
                            builder.pop();
                            continue;
                        }
                        const fieldIndex = context.constantPool.add(fieldName);
                        builder.setProperty(fieldIndex);
                        builder.pop();
                    }
                }
                continue;
            }
            if (!NodeGuards_1.NodeGuards.isMethodDefinitionNode(member)) {
                continue;
            }
            if (member.kind === 'constructor') {
                continue;
            }
            if (member.kind === 'get') {
                if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(member.value)) {
                    if (member.computed) {
                        (_h = context.dispatch) === null || _h === void 0 ? void 0 : _h.expression(member.key);
                        (_j = context.dispatch) === null || _j === void 0 ? void 0 : _j.expression(member.value);
                        const getterOpcode = member.static
                            ? VMOpCode_1.VMOpCode.DEFINE_STATIC_GETTER_COMPUTED
                            : VMOpCode_1.VMOpCode.DEFINE_GETTER_COMPUTED;
                        builder.emitRaw({ op: getterOpcode });
                    }
                    else {
                        const propName = this.getMethodName(member);
                        if (propName) {
                            (_k = context.dispatch) === null || _k === void 0 ? void 0 : _k.expression(member.value);
                            const propIndex = context.constantPool.add(propName);
                            const getterOpcode = member.static ? VMOpCode_1.VMOpCode.DEFINE_STATIC_GETTER : VMOpCode_1.VMOpCode.DEFINE_GETTER;
                            builder.emitRaw({ op: getterOpcode, arg: propIndex });
                        }
                    }
                }
                continue;
            }
            if (member.kind === 'set') {
                if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(member.value)) {
                    if (member.computed) {
                        (_l = context.dispatch) === null || _l === void 0 ? void 0 : _l.expression(member.key);
                        (_m = context.dispatch) === null || _m === void 0 ? void 0 : _m.expression(member.value);
                        const setterOpcode = member.static
                            ? VMOpCode_1.VMOpCode.DEFINE_STATIC_SETTER_COMPUTED
                            : VMOpCode_1.VMOpCode.DEFINE_SETTER_COMPUTED;
                        builder.emitRaw({ op: setterOpcode });
                    }
                    else {
                        const propName = this.getMethodName(member);
                        if (propName) {
                            (_o = context.dispatch) === null || _o === void 0 ? void 0 : _o.expression(member.value);
                            const propIndex = context.constantPool.add(propName);
                            const setterOpcode = member.static ? VMOpCode_1.VMOpCode.DEFINE_STATIC_SETTER : VMOpCode_1.VMOpCode.DEFINE_SETTER;
                            builder.emitRaw({ op: setterOpcode, arg: propIndex });
                        }
                    }
                }
                continue;
            }
            if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(member.value)) {
                if (member.computed) {
                    (_p = context.dispatch) === null || _p === void 0 ? void 0 : _p.expression(member.key);
                    (_q = context.dispatch) === null || _q === void 0 ? void 0 : _q.expression(member.value);
                    const methodOpcode = member.static
                        ? VMOpCode_1.VMOpCode.DEFINE_STATIC_METHOD_COMPUTED
                        : VMOpCode_1.VMOpCode.DEFINE_METHOD_COMPUTED;
                    builder.emitRaw({ op: methodOpcode });
                }
                else {
                    const methodName = this.getMethodName(member);
                    if (methodName) {
                        (_r = context.dispatch) === null || _r === void 0 ? void 0 : _r.expression(member.value);
                        const methodIndex = context.constantPool.add(methodName);
                        const methodOpcode = member.static ? VMOpCode_1.VMOpCode.DEFINE_STATIC_METHOD : VMOpCode_1.VMOpCode.DEFINE_METHOD;
                        builder.emitRaw({ op: methodOpcode, arg: methodIndex });
                    }
                }
            }
        }
    }
    getMemberName(member) {
        const key = member.key;
        if (NodeGuards_1.NodeGuards.isIdentifierNode(key) && !member.computed) {
            return key.name;
        }
        if (NodeGuards_1.NodeGuards.isLiteralNode(key) && typeof key.value === 'string') {
            return key.value;
        }
        return null;
    }
    getMethodName(member) {
        return this.getMemberName(member);
    }
    getPropertyName(member) {
        return this.getMemberName(member);
    }
    createFieldInitStatements(instanceFields) {
        return instanceFields
            .map((field) => {
            if (!field.value || field.computed) {
                return null;
            }
            const fieldName = this.getPropertyName(field);
            if (!fieldName) {
                return null;
            }
            return NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.assignmentExpressionNode('=', NodeFactory_1.NodeFactory.memberExpressionNode(NodeFactory_1.NodeFactory.thisExpressionNode(), NodeFactory_1.NodeFactory.identifierNode(fieldName)), field.value));
        })
            .filter((stmt) => stmt !== null);
    }
    injectInstanceFieldsIntoConstructor(constructor, instanceFields, isDerived) {
        if (instanceFields.length === 0) {
            return constructor;
        }
        const fieldInitStatements = this.createFieldInitStatements(instanceFields);
        if (fieldInitStatements.length === 0) {
            return constructor;
        }
        const originalBody = constructor.body.body;
        let newBody;
        if (isDerived) {
            const superCallIndex = originalBody.findIndex((stmt) => {
                return (NodeGuards_1.NodeGuards.isExpressionStatementNode(stmt) &&
                    NodeGuards_1.NodeGuards.isCallExpressionNode(stmt.expression) &&
                    NodeGuards_1.NodeGuards.isSuperNode(stmt.expression.callee));
            });
            if (superCallIndex !== -1) {
                newBody = [
                    ...originalBody.slice(0, superCallIndex + 1),
                    ...fieldInitStatements,
                    ...originalBody.slice(superCallIndex + 1)
                ];
            }
            else {
                newBody = [...fieldInitStatements, ...originalBody];
            }
        }
        else {
            newBody = [...fieldInitStatements, ...originalBody];
        }
        return {
            ...constructor,
            body: {
                ...constructor.body,
                body: newBody
            }
        };
    }
    createConstructorWithInstanceFields(instanceFields, isDerived) {
        const fieldInitStatements = this.createFieldInitStatements(instanceFields);
        let body;
        if (isDerived) {
            const superCall = NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.callExpressionNode(NodeFactory_1.NodeFactory.superNode(), [NodeFactory_1.NodeFactory.spreadElementNode(NodeFactory_1.NodeFactory.identifierNode('arguments'))]));
            body = [superCall, ...fieldInitStatements];
        }
        else {
            body = fieldInitStatements;
        }
        return NodeFactory_1.NodeFactory.functionExpressionNode([], NodeFactory_1.NodeFactory.blockStatementNode(body));
    }
};
exports.VMClassBytecodeCompiler = VMClassBytecodeCompiler;
exports.VMClassBytecodeCompiler = VMClassBytecodeCompiler = __decorate([
    (0, inversify_1.injectable)(),
    __param(0, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMCompilerUtils)),
    __metadata("design:paramtypes", [typeof (_a = typeof IVMCompilerUtils_1.IVMCompilerUtils !== "undefined" && IVMCompilerUtils_1.IVMCompilerUtils) === "function" ? _a : Object])
], VMClassBytecodeCompiler);
